import type { ApiError } from "@talpipe/types"

/**
 * API numeric response codes — keep in sync with
 * `upiter/app/Sourcery/Api/ApiResponseCodes.php`.
 */
export const ApiResponseCode = {
  OK: 20000,
  CREATED: 20100,
  ACCEPTED: 20200,
  NO_CONTENT: 20400,
  BAD_REQUEST: 40000,
  INVALID_LINK: 40001,
  EXPIRED_LINK: 40002,
  INVALID_TOKEN: 40003,
  EXPIRED_TOKEN: 40004,
  UNVERIFIED: 40005,
  ALREADY_VERIFIED: 40006,
  PASSWORD_AUTH_REQUIRED: 40007,
  UNAUTHORISED: 40100,
  INVALID_CREDENTIALS: 40101,
  FORBIDDEN: 40300,
  NOT_FOUND: 40400,
  DUPLICATE: 40900,
  UNPROCESSABLE_ENTITY: 42200,
  TOO_MANY_REQUESTS: 42900,
  SERVER_ERROR: 50000,
} as const

const MESSAGES: Record<number, string> = {
  [ApiResponseCode.BAD_REQUEST]: "Something didn't look right with that request. Please try again.",
  [ApiResponseCode.INVALID_LINK]: "This link is no longer valid. Request a new one to continue.",
  [ApiResponseCode.EXPIRED_LINK]: "This link has expired. Request a new one to continue.",
  [ApiResponseCode.INVALID_TOKEN]: "This token is no longer valid. Request a new one to continue.",
  [ApiResponseCode.EXPIRED_TOKEN]: "This token has expired. Request a new one to continue.",
  [ApiResponseCode.UNVERIFIED]: "You'll need to verify your email before continuing.",
  [ApiResponseCode.ALREADY_VERIFIED]: "Your email is already verified. Sign in to continue.",
  [ApiResponseCode.PASSWORD_AUTH_REQUIRED]:
    "This account was created with email and password. Please sign in that way.",
  [ApiResponseCode.UNAUTHORISED]: "You'll need to sign in to continue.",
  [ApiResponseCode.INVALID_CREDENTIALS]: "That email or password doesn't match our records.",
  [ApiResponseCode.FORBIDDEN]: "You don't have access to this.",
  [ApiResponseCode.NOT_FOUND]: "We couldn't find what you're looking for.",
  [ApiResponseCode.DUPLICATE]: "This already exists.",
  [ApiResponseCode.UNPROCESSABLE_ENTITY]: "Please review the highlighted fields and try again.",
  [ApiResponseCode.TOO_MANY_REQUESTS]:
    "Too many attempts. Please wait a moment before trying again.",
  [ApiResponseCode.SERVER_ERROR]: "Something went wrong on our end. Please try again in a moment.",
}

function isTechnicalErrorMessage(message: string): boolean {
  const normalized = message.trim()
  if (!normalized) return true

  return (
    /^\[(?:GET|POST|PUT|PATCH|DELETE)\]/i.test(normalized) ||
    /\bfailed to fetch\b/i.test(normalized) ||
    /\bnetwork error\b/i.test(normalized) ||
    /\bload failed\b/i.test(normalized) ||
    /<no response>/i.test(normalized) ||
    /^Request failed\b/i.test(normalized) ||
    /https?:\/\//i.test(normalized)
  )
}

/** Resolves a user-facing message from an API failure. */
export function getApiErrorMessage(error: ApiError | null | undefined, fallback: string): string {
  if (!error) return fallback

  const serverMessage = error.message?.trim()
  if (serverMessage && !isTechnicalErrorMessage(serverMessage)) {
    // Prefer the API's concrete message (e.g. domain 422 copy) over generic code maps.
    if (
      typeof error.code !== "number" ||
      error.code === ApiResponseCode.UNPROCESSABLE_ENTITY ||
      !(error.code in MESSAGES)
    ) {
      return serverMessage
    }
  }

  if (typeof error.code === "number") {
    const mapped = MESSAGES[error.code]
    if (mapped) return mapped

    if (serverMessage && !isTechnicalErrorMessage(serverMessage)) {
      return serverMessage
    }

    return fallback
  }

  return fallback
}
